<template>
  <v-container fluid class="pa-10 bg-white-lighten-4">
    <div>
      <div class="max-w-6xl mx-auto p-6 bg-white">
        <div class="mb-8">
          <h1 class="text-3xl font-bold text-gray-900 mb-2">
            Decision Support System
          </h1>
          <input
            type="text"
            v-model="decisionTitle"
            class="text-xl font-semibold text-gray-700 bg-transparent border-b-2 border-gray-200 focus:border-blue-500 outline-none px-2 py-1"
          />
        </div>

        <div class="flex border-b border-gray-200 mb-6">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="activeTab = tab.id"
            :class="[
              'px-6 py-3 font-medium capitalize',
              activeTab === tab.id
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700',
            ]"
          >
            <v-icon>mdi-file</v-icon>
            {{ tab.name }}
          </button>
        </div>

        <!-- Setup Tab -->
        <div v-if="activeTab === 'setup'" class="space-y-8">
          <!-- Criteria Section -->
          <div class="bg-gray-50 p-6 rounded-lg">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-semibold text-gray-900">
                Decision Criteria
              </h3>
              <button
                @click="addCriterion"
                class="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <v-icon>mdi-plus</v-icon>
                Add Criterion
              </button>
            </div>

            <div class="space-y-3">
              <div
                v-for="criterion in criteria"
                :key="criterion.id"
                class="flex items-center space-x-4 bg-white p-4 rounded-md"
              >
                <input
                  type="text"
                  v-model="criterion.name"
                  class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Criterion name"
                />
                <div class="flex items-center space-x-2">
                  <label class="text-sm text-gray-600">Weight:</label>
                  <input
                    type="number"
                    v-model.number="criterion.weight"
                    class="w-16 px-2 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    min="0"
                    max="100"
                  />
                </div>
                <select
                  v-model="criterion.type"
                  class="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="maximize">Maximize</option>
                  <option value="minimize">Minimize</option>
                </select>
                <button
                  @click="deleteCriterion(criterion.id)"
                  class="p-2 text-red-600 hover:bg-red-50 rounded-md"
                >
                  <v-icon>mdi-trash-can</v-icon>
                </button>
              </div>
            </div>
          </div>

          <!-- Alternatives Section -->
          <div class="bg-gray-50 p-6 rounded-lg">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-semibold text-gray-900">
                Decision Alternatives
              </h3>
              <button
                @click="addAlternative"
                class="flex items-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                <v-icon>mdi-plus</v-icon>
                Add Alternative
              </button>
            </div>

            <div class="space-y-3">
              <div
                v-for="alternative in alternatives"
                :key="alternative.id"
                class="flex items-center space-x-4 bg-white p-4 rounded-md"
              >
                <input
                  type="text"
                  v-model="alternative.name"
                  class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Alternative name"
                />
                <button
                  @click="deleteAlternative(alternative.id)"
                  class="p-2 text-red-600 hover:bg-red-50 rounded-md"
                >
                  <v-icon>mdi-trash-can</v-icon>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Matrix Tab -->
        <div v-if="activeTab === 'matrix'" class="bg-gray-50 p-6 rounded-lg">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">
            Score Matrix (1-10 scale)
          </h3>
          <div class="overflow-x-auto">
            <table class="w-full bg-white rounded-lg shadow-sm">
              <thead>
                <tr class="bg-gray-100">
                  <th class="text-left p-4 font-medium text-gray-900">
                    Alternatives
                  </th>
                  <th
                    v-for="criterion in criteria"
                    :key="criterion.id"
                    class="text-center p-4 font-medium text-gray-900"
                  >
                    <div>{{ criterion.name }}</div>
                    <div class="text-xs text-gray-500">
                      {{ criterion.type }} (weight: {{ criterion.weight }})
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="alternative in alternatives"
                  :key="alternative.id"
                  class="border-t border-gray-200"
                >
                  <td class="p-4 font-medium text-gray-900">
                    {{ alternative.name }}
                  </td>
                  <td
                    v-for="criterion in criteria"
                    :key="criterion.id"
                    class="p-4 text-center"
                  >
                    <input
                      type="number"
                      :value="alternative.scores[criterion.id] || 0"
                      @input="
                        updateScore(
                          alternative.id,
                          criterion.id,
                          $event.target.value
                        )
                      "
                      class="w-16 px-2 py-1 text-center border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      min="1"
                      max="10"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Results Tab -->
        <div v-if="activeTab === 'results'" class="space-y-6">
          <!-- Recommendation -->
          <div
            v-if="recommendation"
            class="bg-blue-50 border border-blue-200 p-6 rounded-lg"
          >
            <div class="flex items-center mb-3">
              <v-icon>mdi-check</v-icon>
              <h3 class="text-lg font-semibold text-blue-900">
                Recommended Choice
              </h3>
            </div>
            <div class="text-blue-800">
              <p class="text-xl font-medium mb-2">
                {{ recommendation.best.name }}
              </p>
              <p>Total Score: {{ recommendation.best.totalScore }}/10</p>
              <p v-if="recommendation.margin > 0" class="text-sm mt-1">
                Leading by {{ recommendation.margin.toFixed(2) }} points
              </p>
            </div>
          </div>

          <!-- Detailed Results -->
          <div class="bg-gray-50 p-6 rounded-lg">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">
              Detailed Analysis
            </h3>
            <div class="space-y-4">
              <div
                v-for="(result, index) in results"
                :key="result.id"
                :class="[
                  'bg-white p-4 rounded-lg border-2',
                  index === 0
                    ? 'border-green-300 bg-green-50'
                    : 'border-gray-200',
                ]"
              >
                <div class="flex justify-between items-center mb-3">
                  <h4 class="font-semibold text-gray-900 flex items-center">
                    <span
                      :class="[
                        'inline-block w-6 h-6 rounded-full text-white text-sm text-center mr-3',
                        index === 0
                          ? 'bg-green-600'
                          : index === 1
                          ? 'bg-blue-600'
                          : 'bg-gray-600',
                      ]"
                    >
                      {{ index + 1 }}
                    </span>
                    {{ result.name }}
                  </h4>
                  <div class="text-xl font-bold text-gray-900">
                    {{ result.totalScore }}/10
                  </div>
                </div>

                <div
                  class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
                >
                  <div
                    v-for="criterion in criteria"
                    :key="criterion.id"
                    class="bg-gray-50 p-3 rounded"
                  >
                    <div class="text-sm font-medium text-gray-700">
                      {{ criterion.name }}
                    </div>
                    <div class="text-xs text-gray-500 mb-1">
                      Raw: {{ result.details[criterion.id].rawScore }}/10
                      <span v-if="criterion.type === 'minimize'">
                        (adj: {{ result.details[criterion.id].adjustedScore }})
                      </span>
                    </div>
                    <div class="text-sm text-gray-900">
                      Weighted:
                      {{ result.details[criterion.id].weightedContribution }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
export default {
  name: "DSS",
  data() {
    return {
      decisionTitle: "My Important Decision",
      criteria: [
        { id: 1, name: "Cost", weight: 30, type: "minimize" },
        { id: 2, name: "Quality", weight: 40, type: "maximize" },
        { id: 3, name: "Time Required", weight: 30, type: "minimize" },
      ],
      alternatives: [
        { id: 1, name: "Option A", scores: { 1: 7, 2: 8, 3: 6 } },
        { id: 2, name: "Option B", scores: { 1: 5, 2: 9, 3: 8 } },
        { id: 3, name: "Option C", scores: { 1: 9, 2: 6, 3: 7 } },
      ],
      results: [],
      activeTab: "setup",
      tabs: [
        { id: "setup", name: "setup", icon: "calculator" },
        { id: "matrix", name: "matrix", icon: "bar-chart-3" },
        { id: "results", name: "results", icon: "check-circle" },
      ],
    };
  },
  computed: {
    recommendation() {
      if (this.results.length === 0) return null;
      const best = this.results[0];
      const margin =
        this.results.length > 1
          ? best.totalScore - this.results[1].totalScore
          : 0;
      return { best, margin };
    },
  },
  watch: {
    criteria: {
      handler() {
        this.calculateResults();
      },
      deep: true,
    },
    alternatives: {
      handler() {
        this.calculateResults();
      },
      deep: true,
    },
  },
  mounted() {
    this.calculateResults();
  },

  methods: {
    calculateResults() {
      const totalWeight = this.criteria.reduce((sum, c) => sum + c.weight, 0);

      const calculatedResults = this.alternatives.map((alt) => {
        let weightedScore = 0;
        let details = {};

        this.criteria.forEach((criterion) => {
          const rawScore = alt.scores[criterion.id] || 0;
          const normalizedWeight = criterion.weight / totalWeight;
          const adjustedScore =
            criterion.type === "minimize" ? 10 - rawScore : rawScore;
          const weightedContribution = adjustedScore * normalizedWeight;

          weightedScore += weightedContribution;
          details[criterion.id] = {
            rawScore,
            adjustedScore,
            weightedContribution: weightedContribution.toFixed(2),
          };
        });

        return {
          id: alt.id,
          name: alt.name,
          totalScore: weightedScore.toFixed(2),
          details,
        };
      });

      calculatedResults.sort((a, b) => b.totalScore - a.totalScore);
      this.results = calculatedResults;
    },
    addCriterion() {
      const newId = Math.max(...this.criteria.map((c) => c.id), 0) + 1;
      this.criteria.push({
        id: newId,
        name: `Criterion ${newId}`,
        weight: 10,
        type: "maximize",
      });
    },
    deleteCriterion(id) {
      this.criteria = this.criteria.filter((c) => c.id !== id);
      this.alternatives.forEach((alt) => {
        delete alt.scores[id];
      });
    },
    addAlternative() {
      const newId = Math.max(...this.alternatives.map((a) => a.id), 0) + 1;
      const newScores = {};
      this.criteria.forEach((c) => {
        newScores[c.id] = 5;
      });
      this.alternatives.push({
        id: newId,
        name: `Option ${String.fromCharCode(65 + this.alternatives.length)}`,
        scores: newScores,
      });
    },
    deleteAlternative(id) {
      this.alternatives = this.alternatives.filter((a) => a.id !== id);
    },
    updateScore(altId, critId, score) {
      const alternative = this.alternatives.find((alt) => alt.id === altId);
      if (alternative) {
        alternative.scores[critId] = parseInt(score) || 0;
      }
    },
  },
};
</script>

<style scoped>
.v-card {
  border-radius: 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}
</style>
